import { CenterModal, ModalCloseTarget } from 'react-spring-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import React, { useState, useEffect } from 'react';
import { Trans } from "react-i18next";
import ReactCountryFlag from "react-country-flag"
import { Box, Button } from 'grommet';
import { Link } from "react-router-dom";
import LanguageTools from '../utils/LanguageTools';
import i18n from "i18next";

const Usher = (props) => {
    const { match, t, setLocale, location } = props;
    const [popupOpen, setPopupOpen] = useState(false);
    const currentLocale = match.params.locale || i18n.language || window.localStorage.i18nextLng || 'en-UK';
    const languageCookie = LanguageTools.getLanguageCookie();

    /**
     * Changes the application's language, updates the language cookie, and optionally dispatches an action.
     * If the selected language is different from the current locale, closes the popup and reloads the page after a short delay to fetch new content.
     *
     * @param {string} lng - The language code to switch to (e.g., 'en', 'fr').
     * @param {function} [dispatch] - Optional dispatch function to update language state.
     */
    const changeLanguage = (lng, dispatch) => {
      i18n.changeLanguage(lng);
      LanguageTools.setLanguageCookie(lng);
      if (dispatch){
        dispatch(lng);
      }
      if (lng !== currentLocale) {
        setPopupOpen(false);
        setTimeout(() => {window.location.reload()}, 500) // Reload to fetch new content from Contentful
      } else {
        setPopupOpen(false);
      }
    };

    /**
     * Detects and returns the suggested locale for the user.
     *
     * Utilizes LanguageTools.sniffLocale() to determine the most appropriate locale.
     *
     * @returns {string} The suggested locale string.
     */
    const getSuggestedLocale = () => {
      const suggestedlocale = LanguageTools.sniffLocale();
      return suggestedlocale;
    }

    /**
     * Returns a React element suggesting a language switch option to the user.
     * 
     * The suggestion is based on the result of `getSuggestedLocale()` compared to the current locale.
     * If the suggested locale matches the current locale and a language cookie is present, 
     * the language from the cookie is suggested. Otherwise, the suggested locale is used.
     * 
     * The returned element is a `<Link>` that, when clicked, changes the application's language.
     * It displays the country flag and name for the suggested language.
     * 
     * @returns {React.ReactNode|null} A React element representing the suggested language option, or null if no suggestion is available.
     */
    const getSuggestedLanguage = () => {
      if (getSuggestedLocale() === currentLocale){
        // When suggested language is same as current
        // it means the cookie locale should be suggested 
        if (languageCookie) {
          return (
            <Link 
              to={LanguageTools.generateLanguage(languageCookie, location)}
              className="usherOption" 
              onClick={() => changeLanguage(languageCookie, setLocale, true)}
            >
                <ReactCountryFlag countryCode={LanguageTools.getIsoCountryFromLocale(languageCookie.toLowerCase())} />
                <div style={{display: "flex", flexDirection: "column", gap: "3px"}}>
                  <b>{ LanguageTools.countryNameFromLocale(languageCookie) }</b>
                  <span style={{opacity: "0.5", fontSize: "12px"}}>
                    <Trans>{ LanguageTools.countryNameFromLocale(languageCookie) }</Trans>
                  </span>
                </div>
            </Link>
          )
        }
      } else {
        return (
          <Link 
            to={LanguageTools.generateLanguage(getSuggestedLocale(), location)}
            className="usherOption" 
            onClick={() => changeLanguage(getSuggestedLocale(), setLocale, true)}
          >
              <ReactCountryFlag countryCode={LanguageTools.getIsoCountryFromLocale(getSuggestedLocale())} />
              <div style={{display: "flex", flexDirection: "column", gap: "3px"}}>
                <b>{ LanguageTools.countryNameFromLocale(getSuggestedLocale()) }</b>
                <span style={{opacity: "0.5", fontSize: "12px"}}>
                  <Trans>{ LanguageTools.countryNameFromLocale(getSuggestedLocale()) }</Trans>
                </span>
              </div>
          </Link>
        )
      }
    }

    /**
     * Renders a button displaying the current locale option, including a country flag,
     * the country name in bold, and a translated country name. When clicked, it triggers
     * a language change using the provided `changeLanguage` function.
     *
     * @function
     * @returns {JSX.Element} A button component representing the current locale option.
     */
    const getCurrentLocaleOption = () => {
      return (
        <Button 
          className="usherOption" 
          onClick={() => changeLanguage(currentLocale, setLocale, true)}
        >
            <ReactCountryFlag countryCode={LanguageTools.getIsoCountryFromLocale(currentLocale)} />
            <div style={{display: "flex", flexDirection: "column", gap: "3px"}}>
              <b>{ LanguageTools.countryNameFromLocale(currentLocale) }</b>
              <span style={{opacity: "0.5", fontSize: "12px"}}>
                <Trans>{ LanguageTools.countryNameFromLocale(currentLocale) }</Trans>
              </span>
            </div>
        </Button>
      )
    }

    // when component mounts, check for existing language cookie
    useEffect(() => {
      if (
            (!languageCookie && getSuggestedLocale() !== currentLocale) 
            || (languageCookie && languageCookie !== currentLocale)
        ){
        setPopupOpen(true);
      }
    }, []);

    return (
        <CenterModal
          isOpen={popupOpen}
          onDismiss={() => setPopupOpen(false)}
          contentTransition={{
            from: { background: 'white', transform: 'translateY(-100%)' },
            enter: { background: 'white', transform: 'translateY(0)' },
            leave: { background: 'white', transform: 'translateY(-100%)' }
          }}
          contentProps={{ style: { borderRadius: "10px"} }}
          className="usherModal"
        >
          <ModalCloseTarget>
            <button className="modalClose modalCloseRight modalCloseHoverBlue"><FontAwesomeIcon icon={faTimesCircle} /></button>
          </ModalCloseTarget>
          <Box id="usher" pad="medium" align="start" >
              <h2>Where would you like to go?</h2>
              <h3><Trans>Where would you like to go?</Trans></h3>
              <Box className="usherOptions">
                  {getCurrentLocaleOption()}
                  {getSuggestedLanguage()}
              </Box>
          </Box>
        </CenterModal>
    );
}

export default Usher;
